import './Bookmarks.scss'

import { useContext, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'

import { LMSKeys, practices } from 'components/common/api/constants'
import {
  ROUTE_ACTIVITIES_JOURNALING_ALL,
  ROUTE_ACTIVITIES_JOURNALING_DAILY_ALL,
  ROUTE_BOOKMARKS,
  ROUTE_TODAY
} from 'components/common/helpers/Paths'
import ScrollingCards from 'components/common/navigation/ScrollingCards'
import ScrollingCardsDays from 'components/common/navigation/ScrollingCardsDays'
import ScrollingCardsPractices from 'components/common/navigation/ScrollingCardsPractices'
import DailyRembleScrollingCards from 'components/daily-remble/DailyRembleScrollingCards'
import CourseContext from 'components/common/contexts/CourseContext'
import { LMS_TYPE_COURSES, LMS_TYPE_TOPIC } from 'components/common/api/Courses'
import {
  KEY_PRACTICE_BREATHWORK_WP,
  KEY_PRACTICE_COPING_SKILLS_WP,
  KEY_PRACTICE_DAILY_REMBLE_WP,
  KEY_PRACTICE_JOURNALING,
  KEY_PRACTICE_JOURNALING_WP,
  KEY_PRACTICE_MEDITATIONS_WP,
  getLMSCategoriesByIds,
  getLMSObjectsByIds
} from 'components/common/api'
import Loading from 'templates/Loading/Loading'
import ScrollingCardsPracticeList from 'components/common/navigation/ScrollingCardsPracticeList/ScrollingCardsPracticeList'

/**
 * BookmarkCollection component. Used to display a collection of bookmarks within a `Library` component
 * Determines which component to render based on the type
 * @param {Array} bookmarks Bookmarks to display
 * @param {String} type The type of bookmarks to display
 * @param {String} baseUrl The base URL to use for the bookmark links
 * @param {String} returnPath The path to return to when clicking on a bookmark
 */
const BookmarkCollection = ({
  bookmarks,
  type,
  returnPath = ROUTE_BOOKMARKS
}) => {
  // Find the practice type
  const practiceType = practices.find(practice => practice.lmsType === type)

  // Set the base URL based on the type
  const baseUrl = practiceType?.path

  // Determine which component to render based on the type
  switch (type) {
    // Courses
    case LMS_TYPE_COURSES:
      return <ScrollingCards courses={bookmarks} returnPath={returnPath} />

    // Sessions
    case LMS_TYPE_TOPIC:
      return <ScrollingCardsDays lessons={bookmarks} returnPath={returnPath} />

    // Daily Remble
    case KEY_PRACTICE_DAILY_REMBLE_WP:
      return (
        <DailyRembleScrollingCards
          dailyRembleVideos={bookmarks}
          returnPath={returnPath}
        />
      )

    // Journaling Prompts
    case KEY_PRACTICE_JOURNALING:
      return (
        <Col>
          <ScrollingCardsPractices
            practices={bookmarks}
            type={KEY_PRACTICE_JOURNALING}
            baseUrl={ROUTE_ACTIVITIES_JOURNALING_ALL}
            returnPath={returnPath}
          />
        </Col>
      )

    // Daily Journals
    case KEY_PRACTICE_JOURNALING_WP:
      return (
        <Col>
          <ScrollingCardsPractices
            practices={bookmarks}
            type={KEY_PRACTICE_JOURNALING_WP}
            baseUrl={ROUTE_ACTIVITIES_JOURNALING_DAILY_ALL}
            returnPath={returnPath}
          />
        </Col>
      )

    // Practices with previews
    case KEY_PRACTICE_BREATHWORK_WP:
    case KEY_PRACTICE_COPING_SKILLS_WP:
    case KEY_PRACTICE_MEDITATIONS_WP:
      return (
        <Col>
          <ScrollingCardsPracticeList
            practices={bookmarks}
            type={type}
            baseUrl={baseUrl}
            returnPath={returnPath}
          />
        </Col>
      )

    // Remaining Practices
    default:
      return (
        <Col>
          <ScrollingCardsPractices
            practices={bookmarks}
            type={type}
            baseUrl={baseUrl}
            returnPath={returnPath}
          />
        </Col>
      )
  }
}

/**
 * LibraryEvent component. Used to display library events within a `Library` component
 */
export default function Library({ cache, setReturnPath, setHeaderTitle }) {
  const { bookmarks, courses } = useContext(CourseContext)

  // Bookmarked item states
  const [isLoading, setIsLoading] = useState(true)
  const [bookmarksByCategory, setBookmarksByCategory] = useState({})

  /**
   * Private helper function to organize the bookmarked items into their respective categories
   */
  const _fetchAndOrganizeBookmarks = async (bookmarks, courses) => {
    let objectIds = [],
      categoryIds = [],
      bookmarkCategories = {}

    // Loop through the bookmarks once to get the unique categories and bookmark IDs
    bookmarks.forEach(bookmark => {
      bookmarkCategories[bookmark.category] = []

      // If the category type is not a category, then append the bookmark ID to the array
      if (LMSKeys[bookmark.category]?.isCategory)
        categoryIds.push(bookmark.category_object_id)
      else objectIds.push(bookmark.category_object_id)
    })

    // Get all bookmarked objects
    if (objectIds.length) {
      const bookmarkedObjects = await getLMSObjectsByIds(objectIds)

      // Loop through the bookmarked objects and organize them into their respective categories
      bookmarkedObjects.forEach(bookmarkedObject => {
        bookmarkCategories[bookmarkedObject.post_type]?.push(bookmarkedObject)
      })
    }

    // Get all bookmarked categories
    if (categoryIds.length) {
      const categories = await getLMSCategoriesByIds(categoryIds)

      // Loop through the bookmarked categories and organize them into their respective categories
      categories.forEach(category => {
        // Find the bookmark category that matches the category ID
        const bookmarkCategory = bookmarks.find(
          bookmark => bookmark.category_object_id === category.term_id
        )

        // If we don't have a bookmark category, then return early
        if (!bookmarkCategory) return

        // Add the category to the bookmark categories
        bookmarkCategories[bookmarkCategory.category]?.push(category)
      })
    }

    setBookmarksByCategory(bookmarkCategories)
    setIsLoading(false)
  }

  useEffect(() => {
    setReturnPath(ROUTE_TODAY)
    setHeaderTitle('Bookmarks')

    if (!bookmarks || !courses || !cache) return

    _fetchAndOrganizeBookmarks(bookmarks, courses)
  }, [bookmarks, courses, cache, setReturnPath, setHeaderTitle])

  return (
    <Loading isLoaded={isLoading}>
      <Container className='library-wrapper'>
        {/* If we don't have any bookmarks at all, let's show a message */}
        {!bookmarks?.length ? (
          <div className='library-empty'>
            <h2 className='library-empty__title'>
              You haven't bookmarked anything yet
            </h2>
            <p className='library-empty__description'>
              Bookmark content to save it for later.
            </p>
          </div>
        ) : (
          <>
            {Object.keys(LMSKeys).map(key => {
              const category = LMSKeys[key]
              const categoryItems = bookmarksByCategory[key]

              // Return early if there are no bookmarks for this category
              if (!categoryItems?.length) return null

              return (
                <Row key={key}>
                  <h3 className='subtitle mb-0'>{category.title}</h3>
                  <BookmarkCollection type={key} bookmarks={categoryItems} />
                </Row>
              )
            })}
          </>
        )}
      </Container>
    </Loading>
  )
}
